import type { ValueOf } from '~/interfaces';

export const formatNumber = (number: number | string) =>
  number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');

export const formatAmount = (number: number | string, placeholder = '-') => {
  if (!number) return placeholder;
  return `${formatNumber(number)}₽`;
};

export const transferValues = (from: any, to: any, excludeKeys: string[] = []) => {
  // Перенести все значения, включая его отсутствие
  const setPrimitive = (key: string, value: any) => {
    to[key] = value;
  };
  const setArray = (key: string, value: any) => {
    to[key] = [...value];
  };
  const setObject = (key: string, value: any) => {
    to[key] = { ...value };
  };
  Object.entries(from).forEach(([key, value]) => {
    if (key in to && !excludeKeys.includes(key)) {
      const isArray = value instanceof Array;
      if (isArray) return setArray(key, value);
      const isObject = typeof value === 'object';
      if (isObject) return setObject(key, value);
      setPrimitive(key, value);
    }
  });
};

export const getMapFromArray = <T extends object>(
  arrayToMap: T[],
  unrequiredMapKey = 'id' as keyof T,
) => {
  const reduceToEntries = arrayToMap.reduce((acc: [ValueOf<T>, T][], curr: T) => {
    let mapKey = unrequiredMapKey;
    const newEntity = typeof curr[unrequiredMapKey] === 'undefined';
    if (newEntity) mapKey = 'keyId' as keyof T;
    return [...acc, [curr[mapKey], curr]] as [ValueOf<T>, T][];
  }, []) as [ValueOf<T>, T][];
  // @ts-ignore
  return new Map([...reduceToEntries]);
};

export const sliceString = (toSlice: string, lim = 30) => {
  if (toSlice.length > lim) {
    return `${toSlice.slice(0, lim)}...`;
  }
  return toSlice;
};

export const getFormattedParameter = (count: number | string) => {
  const sanitizedCount = count.toString().trim();
  const parsedCount = Number(sanitizedCount);
  if (isNaN(parsedCount)) return '-';
  const countLength = sanitizedCount.length;
  if (countLength > 6) {
    return `${formatNumber((parsedCount / 1e6).toFixed(1))}М`;
  }
  if (countLength < 4) {
    return formatNumber(parsedCount);
  }
  return `${formatNumber((parsedCount / 1e3).toFixed(1))}К`;
};

export const sliceDate = (dateString: string) => dateString.slice(0, 10);

export const formatDate = (dateString: string) => {
  if (dateString.indexOf('-') === -1 || dateString.length < 10) {
    console.log('Неожиданный аргумент');
    return '';
  }

  try {
    return sliceDate(dateString).split('-').toReversed().join('.');
  } catch {
    return '';
  }
};

export const validateUrl = (url: string) => {
  try {
    (() => new URL(url))();
    return true;
  } catch {
    return 'Укажите корректную ссылку формата https:// ....';
  }
};

export const ensureProtocol = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const calcSmallWidth = () => window.innerWidth <= 1000;

export const setToLocalStorage = <T>(name: string, value: T): void => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(name, JSON.stringify(value));
};

export const getFromLocalStorage = <T>(name: string): T | null => {
  if (typeof localStorage === 'undefined') return null;
  const item = localStorage.getItem(name);
  if (item !== null && item !== 'undefined') {
    try {
      return JSON.parse(item);
    } catch (error) {
      console.log(error);
    }
  }
  return null;
};

export const removeFromLocalStorage = (name: string): boolean => {
  const haveItem = getFromLocalStorage(name) !== null;
  if (haveItem) {
    localStorage.removeItem(name);
    return true;
  }
  return false;
};

/**
 * Определяет placeType по URL канала:
 * 1 — Telegram, 2 — VK, 3 — Max
 */
export const getPlaceTypeFromUrl = (url: string): number => {
  if (!url) return 1;
  const lower = url.toLowerCase();
  if (lower.includes("vk.com") || lower.includes("vk.ru")) return 2;
  if (lower.includes("max.ru")) return 3;
  return 1;
};

export const fmtNum = (num: number | null, isNumber = false): string | null => {
  if (num === null) return null;
  const absNum = Math.round(Math.abs(num));
  if (absNum >= 1_000_000) {
    if (isNumber) return (num / 1_000_000).toFixed(1);
    return `${(num / 1_000_000).toFixed(1)}м`;
  } else if (absNum >= 100_000) {
    if (isNumber) return (num / 1_000).toFixed(1);
    return `${Math.round(num / 1_000)}к`;
  } if (absNum >= 1_000) {
    if (isNumber) return (num / 1_000).toFixed(1);
    return `${(num / 1_000).toFixed(1)}к`;
  }
  return absNum.toString();
};

/**
 * Форматирует оставшееся время в формат "Xд Xч Xм"
 * @param diffInSeconds - разница в секундах
 * @returns отформатированная строка времени
 */
export const formatTimer = (diffInSeconds: number): string => {
  if (diffInSeconds < 0) return "0ч 0м";
  
  const days = Math.floor(diffInSeconds / 86400);
  const hours = Math.floor((diffInSeconds % 86400) / 3600);
  const minutes = Math.floor((diffInSeconds % 3600) / 60);
  
  if (days > 0) {
    if (hours > 0 && minutes > 0) {
      return `${days}д ${hours}ч ${minutes}м`;
    } else if (hours > 0) {
      return `${days}д ${hours}ч`;
    } else if (minutes > 0) {
      return `${days}д ${minutes}м`;
    }
    return `${days}д`;
  }
  
  if (hours > 0 && minutes > 0) {
    return `${hours}ч ${minutes}м`;
  } else if (hours > 0) {
    return `${hours}ч`;
  }
  
  return `${minutes}м`;
};
// «1 канал», «2 канала», «5 каналов»
export const pluralize = (count: number, forms: [string, string, string]): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};
